/**
 * ResultsHeader Component
 * Displays the main score cards and tab navigation
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ResultData, TabType } from '../../types/results';

interface ResultsHeaderProps {
  results: ResultData;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

const TABS: { key: TabType; label: string }[] = [
  { key: 'summary', label: 'Résumé' },
  { key: 'profile', label: 'Profil' },
  { key: 'recommendations', label: 'Recommandations' },
  { key: 'dimensions', label: 'Dimensions' },
  { key: 'answers', label: 'Réponses' },
  { key: 'times', label: 'Temps' },
];

export const ResultsHeader: React.FC<ResultsHeaderProps> = ({ 
  results, 
  activeTab, 
  onTabChange 
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-lg font-bold text-gray-800">
          Résultats - Challenge {results.version}
        </h1>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-primary hover:bg-opacity-90 text-white font-semibold py-1.5 px-4 rounded-lg text-sm transition"
        >
          Retour
        </button>
      </div>
      
      {/* Main score cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <ScoreCard
          label="Score Final"
          value={`${results.total_score}/${results.max_score}`}
          subtext={`${results.percentage}%`}
          bgColor="bg-primary bg-opacity-10"
          textColor="text-primary"
        />
        <ScoreCard
          label="Correctes"
          value={results.correct_answers.toString()}
          subtext={`/${results.total_questions}`}
          bgColor="bg-green-50"
          textColor="text-green-600"
        />
        <ScoreCard
          label="Incorrectes"
          value={results.incorrect_answers.toString()}
          subtext={`${results.skipped_answers} passees`}
          bgColor="bg-red-50"
          textColor="text-red-600"
        />
        <ScoreCard
          label="Duree"
          value={results.total_time_formatted}
          subtext="total"
          bgColor="bg-secondary bg-opacity-10"
          textColor="text-secondary"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            className={`px-3 py-2 text-sm font-medium whitespace-nowrap transition ${
              activeTab === tab.key
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Sub-component for score cards
interface ScoreCardProps {
  label: string;
  value: string;
  subtext: string;
  bgColor: string;
  textColor: string;
}

const ScoreCard: React.FC<ScoreCardProps> = ({ 
  label, 
  value, 
  subtext, 
  bgColor, 
  textColor 
}) => (
  <div className={`${bgColor} p-3 rounded-lg text-center`}>
    <p className="text-gray-600 text-xs font-medium">{label}</p>
    <p className={`text-xl font-bold ${textColor}`}>{value}</p>
    <p className="text-sm text-gray-500">{subtext}</p>
  </div>
);

export default ResultsHeader;
